import { ValidationError, ValidationResult, ValidatorFn, ValidatorOptions } from ".";

export const createValidator = <T>(
    validationFn: (value: T, options?: ValidatorOptions) => boolean,
    errorCode: ValidationError['code'],
    errorMessageFn: (options: ValidatorOptions) => string,
    transform?: (value: T) => T
): ValidatorFn<T> => {
    return (value, options = {}) => {
        const { fieldName = 'Field' } = options;

        if ((value === null || value === undefined) && options.required) {
            return {
                isValid: false,
                value: null,
                errors: [{
                    code: 'missing_data',
                    message: `${fieldName} is required but was not provided`,
                    field: fieldName
                }]
            };
        }

        // Skip validation for null/undefined when not required
        if (value === null || value === undefined) {
            return {
                isValid: true,
                value: null,
                errors: []
            };
        }

        const isValid = (value as T) && validationFn(value as T, options);

        return {
            isValid,
            value: isValid ? (transform ? transform(value as T) : value as T) : null,
            errors: isValid ? [] : [{
                code: errorCode,
                message: errorMessageFn(options),
                field: fieldName,
                context: options.context
            }]
        };
    };
};

export const validate = <T>(
    value: unknown,
    validators: ValidatorFn<T>[],
    options?: ValidatorOptions
): ValidationResult<T> => {
    let isValid = true;
    const allErrors: ValidationError[] = [];

    // Run all validators and collect all errors
    for (const validator of validators) {
        const result = validator(value, options);
        if (!result.isValid) {
            isValid = false;
            allErrors.push(...result.errors);
        }
    }

    return {
        isValid,
        value: isValid ? value as T : null,
        errors: allErrors
    };
};

export const validateObjectKeys = <T extends Record<string, unknown>>(
    object: Record<string, unknown>,
    validators: ValidatorFn<T>[],
    options?: ValidatorOptions
): Record<string, ValidationResult<T>> => {
    const results: Record<string, ValidationResult<T>> = {};

    for (const key in object) {
        const value = object[key];
        const result = validate(value, validators, { ...options, fieldName: key });
        results[key] = result;
    }
    return results;
}